import React, { useState, useEffect, useMemo, useRef } from "react"
import { Box, IconButton, Paper, Hidden, useTheme, useMediaQuery } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import { ArrowBackOutlined } from "@material-ui/icons"
import { makeStyles } from "@material-ui/styles"
import { useHistory, useLocation } from "react-router-dom"
import { Trans, t } from "@lingui/macro"
import queryString from "query-string"
import {
  Avatar,
  TimeAgo,
  TooltipIcon,
  ExpansionPanel,
  QRButton,
  CopyLinkButton,
  AuthorTools,
  ModuleHeading
} from "../.."
import { RowBox } from "../../Boxes"
import { KNOWLEDGE_TYPE, useMutationUpdateKnowledge } from "../../../data"
import { toId, mapToIds } from "../../../utils"
import LazyKnowledgeInlineEditor from "../LazyKnowledgeInlineEditor"
import { KnowledgeArticleSaveButton } from "../KnowledgeArticleSaveButton"
import { transformContentForSaving } from "../../../utils/knowledgeArticleConvertor"
import { useAuth } from "../../../services"
import FloatingActionBar from "../../FloatingActionBar/FloatingActionBar"
import { renderDesktopButtons, useStyles as useButtonStyles } from "../KnowledgeArticleButtons"
import { useUnsavedChangesAlert } from "../../../utils/useUnsavedChangesAlert"
import { CategoryBreadcrumbs } from "../CategoryBreadcrumbs"
import { TableOfContents } from "./TableOfContents"
import useSnackbar from "../../SnackbarProvider/useSnackbar"
import { addRecentItem } from "../../../utils/useRecentlyViewed"

const useStyles = makeStyles((theme) => ({
  articleHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    justifyContent: "space-between"
  },
  authorPanel: {
    background: theme.palette.background.default
  },
  authorContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  by: {
    color: theme.palette.text.secondary
  },
  trainingIcon: {
    display: "flex",
    flexDirection: "row"
  },
  icons: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1)
  },
  listItemTocExpansion: {
    padding: theme.spacing(0, 2)
  }
}))

const useExpansionStyles = makeStyles((theme) => ({
  root: {
    "&:last-child": {
      borderRadius: theme.radius(1)
    }
  }
}))

const useExpansionSummaryStyles = makeStyles((theme) => ({
  content: {
    paddingLeft: theme.spacing(2)
  },
  expandIcon: {
    marginRight: theme.spacing(1)
  }
}))

const useExpansionDetailsClasses = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(0),
    padding: theme.spacing(0)
  }
}))

const FullArticle = ({
  knowledge,
  hasRestrictedToKiosk,
  restrictedMessage,
  scrollTo,
  onEdit,
  onDelete,
  onCopy,
  hasGroupsOrLocations,
  onUpdateTableOfContents,
  contentTableOfContents = []
}) => {
  const [editing, setEditing] = useState(false)
  const [updatedContent, setUpdatedContent] = useState(knowledge.content)
  const [updateKnowledge] = useMutationUpdateKnowledge()
  const [loading, setLoading] = useState(false)
  const [copyLoading, setCopyLoading] = useState(false)
  const [isPanelExpanded, setIsPanelExpanded] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [canCopy, setCanCopy] = useState(false)
  const [canDelete, setCanDelete] = useState(false)
  const [hasContentChanged, setHasContentChanged] = useState(false)
  const { hasPermission, isCurrentUser, isCurrentUserInRole, hasEveryLocation } = useAuth()
  const editorRef = useRef(null)
  const location = useLocation()
  const snackbar = useSnackbar()

  useEffect(() => {
    if (knowledge) {
      addRecentItem(knowledge)
    }
  }, [knowledge])

  // Use the unsaved changes alert hook with its built-in alert component
  const { UnsavedChangesAlert } = useUnsavedChangesAlert(hasContentChanged, {
    alertTitle: t`Unsaved changes`,
    alertMessage: t`Are you sure you want to leave this page? Any unsaved changes to this article will be lost.`,
    okButtonLabel: t`Discard changes`,
    cancelButtonLabel: t`Cancel`,
    okButtonProps: { color: "secondary" }
  })

  const theme = useTheme()
  const history = useHistory()
  const buttonClasses = useButtonStyles({ theme })
  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const classes = useStyles({ xs })
  const expansionClasses = useExpansionStyles()
  const expansionSummaryClasses = useExpansionSummaryStyles()
  const expansionDetailsClasses = useExpansionDetailsClasses()

  const {
    title,
    category,
    content,
    author,
    createdAt,
    updatedAt,
    isRestrictedToKiosk,
    deleted,
    tags,
    locations,
    groups
  } = knowledge

  // Auto-scroll to heading if URL parameter is present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const headingId = searchParams.get("heading")

    if (headingId && scrollTo) {
      // Wait a bit for the content to render before scrolling
      const timeoutId = setTimeout(() => {
        const targetElement = document.getElementById(headingId)
        if (targetElement) {
          // Create a fake event object for the scrollTo function
          const fakeEvent = { preventDefault: () => {} }
          scrollTo(fakeEvent, headingId)
        }
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [location.search, scrollTo, content])

  useEffect(() => {
    if (!canEdit) return

    const qs = queryString.parse(location.search)
    const isNewArticle = qs.new === "true"

    if (isNewArticle) {
      setEditing(true)
    }
  }, [canEdit, location.search])

  // Updated to match AuthorTools.js permission pattern
  useEffect(() => {
    const isUser = isCurrentUser(knowledge.author)
    const isSysadmin = isCurrentUserInRole("sysadmin")

    // Use the same pattern as in AuthorTools.js for consistency
    setCanEdit(
      onEdit &&
        ((isUser && hasPermission("knowledge_update_author")) ||
          hasPermission("knowledge_update_all") ||
          isSysadmin ||
          (hasPermission("knowledge_update_locations") &&
            knowledge.locations.length > 0 &&
            hasEveryLocation(knowledge.locations)))
    )
    setCanCopy(
      onCopy &&
        ((isUser && hasPermission("knowledge_update_author") && hasPermission("knowledge_create")) ||
          hasPermission("knowledge_update_all") ||
          isSysadmin)
    )
    setCanDelete(
      onDelete &&
        ((isUser && hasPermission("knowledge_delete_author")) ||
          hasPermission("knowledge_delete_all") ||
          isSysadmin ||
          (hasPermission("knowledge_delete_locations") &&
            knowledge.locations.length > 0 &&
            hasEveryLocation(knowledge.locations)))
    )
  }, [
    knowledge.author,
    hasPermission,
    isCurrentUser,
    isCurrentUserInRole,
    onCopy,
    onDelete,
    onEdit,
    hasEveryLocation,
    knowledge.locations
  ])

  const renderTableOfContents = useMemo(() => {
    if (contentTableOfContents?.length) {
      return (
        <Hidden lgUp>
          <Box>
            <ExpansionPanel
              title={<Trans>Contents</Trans>}
              expanded={isPanelExpanded}
              onChange={() => setIsPanelExpanded(!isPanelExpanded)}
              expansionClasses={expansionClasses}
              expansionSummaryClasses={expansionSummaryClasses}
              expansionDetailsClasses={expansionDetailsClasses}
            >
              <TableOfContents
                contentTableOfContents={contentTableOfContents}
                expanded={isPanelExpanded}
                classes={classes}
                scrollTo={scrollTo}
              />
            </ExpansionPanel>
          </Box>
        </Hidden>
      )
    }

    return null
  }, [
    contentTableOfContents,
    classes,
    scrollTo,
    isPanelExpanded,
    setIsPanelExpanded,
    expansionClasses,
    expansionSummaryClasses,
    expansionDetailsClasses
  ])

  const handleChangeContent = (newContent) => {
    setUpdatedContent(newContent)
    // only update for once
    !hasContentChanged && editing && setHasContentChanged(true)
  }

  const handleSaveContent = async () => {
    setLoading(true)
    try {
      const cleanedContent = updatedContent.map(transformContentForSaving)
      await updateKnowledge({
        variables: {
          id: toId(knowledge),
          input: {
            content: cleanedContent.filter((item) => item !== null),
            type: KNOWLEDGE_TYPE.ARTICLE,
            title,
            category: category.id,
            isRestrictedToKiosk,
            tags: mapToIds(tags),
            locations: locations.includes("all") ? [] : mapToIds(locations),
            groups: groups.includes("all") ? [] : mapToIds(groups)
          }
        }
      })

      setEditing(false)
      setHasContentChanged(false)
      snackbar.showMessage({ message: t`Article saved successfully` })
    } catch (error) {
      console.error("Error updating knowledge article:", error)
      snackbar.showMessage({ message: t`Failed to save article. Please try again.` })
      setHasContentChanged(true) // Restore changed state
    } finally {
      setLoading(false)
    }
  }

  const handleCopyArticle = async () => {
    try {
      if (!canCopy) {
        return
      }

      setCopyLoading(true)
      await onCopy(knowledge)
      snackbar.showMessage({ message: t`Article copied successfully` })
    } catch (error) {
      console.error("Error creating article copy:", error)
      snackbar.showMessage({ message: t`Failed to copy article. Please try again.` })
    } finally {
      setCopyLoading(false)
    }
  }

  const handleCopyArticleWithNewContent = async () => {
    if (!canCopy || !knowledge) {
      return
    }
    setCopyLoading(true)
    await onCopy(knowledge)
    setCopyLoading(false)
  }

  const buttonText = useMemo(() => {
    if (loading) return <Trans>Saving...</Trans>
    if (editing) return <Trans>Save</Trans>
    return <Trans>Edit content</Trans>
  }, [loading, editing])

  const handleBack = () => {
    history.push(`/knowledge/category/${category.id}`)
  }

  const handleCancel = () => {
    setEditing(false)
    setHasContentChanged(false)
  }

  const handleUpdateTableOfContents = (updatedTableOfContents) => {
    onUpdateTableOfContents(updatedTableOfContents)
  }

  const actionButtons = (
    <Box display="flex" alignItems="center" flexDirection="row">
      <QRButton value={window.location.href} />
      <CopyLinkButton />
      <AuthorTools
        item={knowledge}
        author={author.id}
        onEdit={canEdit ? onEdit : undefined}
        onDelete={canDelete ? onDelete : undefined}
        onCopy={canCopy ? handleCopyArticle : undefined}
        admins={{
          create: "knowledge_create",
          edit: "knowledge_update_all",
          delete: "knowledge_delete_all"
        }}
      />
    </Box>
  )

  // Simple drag and drop handlers for non-editing mode
  const handleDragOver = (e) => {
    if (!editing) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleDrop = (e) => {
    if (!editing) {
      e.preventDefault()
      e.stopPropagation()

      // Check if files were dropped
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        snackbar.showMessage({ message: t`To upload files to this article, please enter editing mode first` })
      }
    }
  }

  if (hasRestrictedToKiosk) {
    return restrictedMessage
  }

  return (
    <Box data-cy="KnowledgeArticle-full" id="knowledge" ref={editorRef} onDragOver={handleDragOver} onDrop={handleDrop}>
      {deleted && (
        <Box mb={1}>
          <Alert severity="warning">
            <Trans>This content has been archived</Trans>
          </Alert>
        </Box>
      )}

      {/* Title display on large screens */}
      {!xs && (
        <RowBox justifyContent="space-between" className={classes.articleHeader} mb={1}>
          <ModuleHeading noMargin>{title}</ModuleHeading>
          {actionButtons}
        </RowBox>
      )}

      {/* Title display on small screens */}
      {xs && (
        <>
          <RowBox justifyContent="space-between" mb={1}>
            <IconButton onClick={handleBack} edge="start">
              <ArrowBackOutlined />
            </IconButton>
            {actionButtons}
          </RowBox>
          <Box mb={2}>
            <ModuleHeading noMargin>{title}</ModuleHeading>
          </Box>
        </>
      )}

      {/* Display breadcrumbs on desktop */}
      {!xs && (
        <CategoryBreadcrumbs ancestors={[...category?.ancestors, ""]} breadcrumbs={[...category?.breadcrumbs, title]} />
      )}
      <Box mb={3}>
        <Paper elevation={0} className={classes.authorPanel}>
          <RowBox className={classes.authorContent}>
            <Box display="flex" alignItems="center">
              <Avatar {...author} />
              <Box ml={1} className={classes.by}>
                <span className="notranslate">
                  <Trans>By</Trans> {author.firstName} {author.lastName} | <TimeAgo date={updatedAt || createdAt} />{" "}
                </span>
              </Box>

              {(hasGroupsOrLocations || isRestrictedToKiosk) && (
                <Box className={classes.icons} ml={0.5}>
                  {hasGroupsOrLocations && (
                    <TooltipIcon
                      name="restricted"
                      refClassName={classes.trainingIcon}
                      tooltip={<Trans>Restricted to locations and/or areas of work</Trans>}
                    />
                  )}
                  {isRestrictedToKiosk && (
                    <TooltipIcon
                      name="phone-link-lock"
                      refClassName={classes.trainingIcon}
                      tooltip={<Trans>Restricted to access on kiosk devices</Trans>}
                    />
                  )}
                </Box>
              )}
            </Box>
            <Hidden smDown>
              {canEdit && (
                <KnowledgeArticleSaveButton
                  isEditing={editing}
                  loading={loading}
                  copyLoading={copyLoading}
                  onCancel={handleCancel}
                  onSaveNewVersion={handleCopyArticleWithNewContent}
                  canCopy={canCopy}
                  canEdit={canEdit}
                  onSave={() => {
                    if (editing) {
                      handleSaveContent()
                      return
                    }
                    setEditing(true)
                  }}
                />
              )}
            </Hidden>
          </RowBox>
        </Paper>
      </Box>

      <Box mb={2}>{renderTableOfContents}</Box>
      {!editing && (
        <LazyKnowledgeInlineEditor
          initialContent={content}
          contentOnly
          onUpdateTableOfContents={handleUpdateTableOfContents}
        />
      )}
      {editing && (
        <LazyKnowledgeInlineEditor
          initialContent={content}
          onChangeContent={handleChangeContent}
          onSave={handleSaveContent}
          onCancel={() => setEditing(false)}
          onSaveNewVersion={handleCopyArticleWithNewContent}
          canEdit={canEdit}
          canCopy={canCopy}
          loading={loading}
          copyLoading={copyLoading}
          currentKnowledgeId={knowledge.id}
        />
      )}

      {editing && canEdit && !xs && (
        <FloatingActionBar from={0}>
          <RowBox justifyContent="space-between" alignItems="center" style={{ width: "100%" }}>
            <Box flex={1}>
              <Trans>Save changes to this article?</Trans>
            </Box>
            {renderDesktopButtons({
              classes: buttonClasses,
              canEdit,
              canCopy,
              isEditing: true,
              loading,
              copyLoading,
              onSave: handleSaveContent,
              onCancel: () => setEditing(false),
              onSaveNewVersion: handleCopyArticleWithNewContent,
              buttonText
            })}
          </RowBox>
        </FloatingActionBar>
      )}

      <UnsavedChangesAlert />
    </Box>
  )
}

export { FullArticle }
